import { markRaw } from "vue";
import { useAuthentication } from "./Authentication";
import { useSharedSlides } from "../SharedSlides/SharedSlideDefinitions";
import { useStore } from "@utilities/UseStore";
import { DefaultSections } from "./DefaultSections";
import { RATIO } from "./Settings";
import type { IPresentation, IPresentationSettings, IPresentationTheme } from "./IPresentation";
import type { SectionMeta } from "./SectionMeta";
import type { Page } from "./Page";

import Title from "../Templates/Title.vue";
import SlideSorter from "../Components/SlideSorter.vue";

export const startPresentation = ( settings: IPresentationSettings ) => 
{
    const context = usePresentation();
    context.start( settings );
    return context;
};

const DefaultTheme: IPresentationTheme = {
    logo: '/carbucks-driven-by-presidian.png',
    textColor: '#ffffff',
    headerColor: '#c43a4f',
    bgColorFrom: '#1d1f48',
    bgColorTo: '#363483',
    navColorFrom: '#33327c',
    navColorTo: '#393684',
    navBorderColor: undefined,
    nextButtonBackground: '#0656d4',
    nextButtonText: '#ffffff',
    triggerButtonBackground: '#10b981',
    triggerButtonText: '#ffffff',
    showImageOnTitlePages: true,
    showHorizonLine: true,
    keyMessageColor: '#10b981'
}

export const usePresentation = () => useStore( 'presentation-context', class implements IPresentation
{

    key = '';
    title = '';
    password = '';
    sections: SectionMeta[] = [];
    theme = DefaultTheme;
    ratio = RATIO;
    isTabletMode = false;

    active = null as any as string;
    currentSection = null as any as SectionMeta;
    currentPage = null as any as Page;
    pageIndex = 0;
    isImagePreloadComplete = false;
    triggers = 0;
    currentTrigger = -1;
    isTitleComponent = false;
    isTransitioning = false;
    isAtEnd = false;
    isContentPage = false;
    isTitlePage = false;
    displayPageTitle = false;
    backgroundImage = null as any as string;
    isReady = false;
    enableNextSlideIndicator = false;
    imageCount = 0;
    imagesLoaded = 0;
    showMedia = false;
    showNavigation = false;
    mediaSrc = '';

    reset() { this.goto( DefaultSections.Start ) };
    registerTrigger( count: number = 1 ) { this.triggers += count };

    canProceed?(): boolean;
    onBeforeLeave?(): boolean;
    onSetNext?(): { section: string, page: number } | null;

    /**
     * Initialises the presentation
     */
    start( settings: IPresentationSettings )
    {

        this.isReady = false;

        this.key = settings.key;
        this.sections = this.initialiseSlides( settings.sections );
        this.title = settings.title;
        this.password = settings.password;
        this.theme = { ...DefaultTheme, ...settings.theme ?? {} };
        this.enableNextSlideIndicator = settings.enableNextSlideIndicator;
        this.ratio = settings.ratio ?? RATIO;
      
        const auth = useAuthentication();
        auth.check( this.key );
        
        const urlParams = new URLSearchParams( window.location.search );
        const s = urlParams.get( 's' );

        this.active = ( s == DefaultSections.Start || s == DefaultSections.End || s == null )
            ? DefaultSections.Start
            : s;

        if( this.sections.find( s => s.name == this.active ) == null ) this.active = DefaultSections.Start;

        this.preloadImages();
        this.goto( this.active, 0 );

    }

    /**
     * Toggles the fullscreen state
     * @returns 
     */
    toggleFullscreen()
    {
        if( !window.screenTop && !window.screenY ) 
        {
            document.exitFullscreen();
            return;
        }   
        document.documentElement.requestFullscreen();
    }
        
    /**
     * Toggles the tablet state
     * @returns 
     */
    toggleTabletMode()
    {
        this.isTabletMode = !this.isTabletMode;
    }

    /**
     * Goto the section and page
     * @param s 
     * @param page 
     */
    goto( section: string, page: number = 0 )
    {

        if( section != this.active ) this.isTransitioning = true;

        const nextSection = this.sections.find( s => s.name == section )!;
        const nextPage = nextSection!.pages[ page ];

        this.active = section;
        this.showNavigation = this.isReady && this.currentPage.hideNavigation !== true && nextPage.hideNavigation !== true;

        setTimeout( () => {
            this.isTitleComponent = false;
            this.backgroundImage = '';
            this.triggers = 0;
            this.currentTrigger = -1;
            this.onBeforeLeave = undefined;
            this.onSetNext = undefined;
            this.canProceed = undefined;
        }, 140 )

        setTimeout( () => {
            this.currentSection = this.sections.find( s => s.name == section )!;
            this.pageIndex = page;
            this.currentPage = this.currentSection!.pages[ this.pageIndex ];
            this.isAtEnd = ( this.currentSection!.name == DefaultSections.End || ( this.currentSection!.name == this.sections[ this.sections.length - 1 ].name ) ) && this.pageIndex == this.currentSection!.pages.length - 1;
            this.isTitlePage = this.isTitleComponent;
            this.isContentPage = this.isTitlePage == false && this.active != DefaultSections.Start && this.active != DefaultSections.End && this.currentSection!.name != DefaultSections.Start && this.currentSection!.name != DefaultSections.End;
            this.displayPageTitle = this.currentSection!.displayPageTitle && this.isTitlePage == false;
        }, 150 )

        setTimeout( () => {
            this.showNavigation = this.currentPage.hideNavigation !== true;
            this.isTransitioning = false
            this.isReady = true;
        }, 500 )

    }

    /**
     * Go back one page
     * @returns 
     */
    back()
    {
        // Move back to the previous page
        if( this.pageIndex > 0 )
        {
            this.goto( this.active!, this.pageIndex - 1 );
            return;
        }

        // Move back to the previous section and set the page to the end index
        const sectionIndex = this.sections.findIndex( s => s.name == this.active );
        if( sectionIndex > 0 )
        {
            this.goto( this.sections[ sectionIndex - 1 ].name, this.sections[ sectionIndex - 1 ].pages.length - 1 );
        }
    }

    /**
     * Goto the next page or advance the trigger animations
     * @returns 
     */
    next()
    {

        // First check that they are allowed to proceed
        if( this.canProceed != null && this.canProceed() === false ) return;
        
        // Advance the current trigger
        if( this.triggers > 0 && this.currentTrigger < this.triggers - 1 )
        {
            this.currentTrigger += 1;
            return;
        }

        if( typeof this.onBeforeLeave == 'function' && this.onBeforeLeave() !== true )
        {
            return;
        }

        // Advance to the page determined by the component we're leaving
        if( typeof this.onSetNext == 'function' )
        {
            const next = this.onSetNext();
            if( next != null )
            {
                this.goto( next.section, next.page );
                return;
            }
        }

        // Advance to the next page
        if( this.pageIndex < this.currentSection!.pages.length - 1 )
        {
            this.goto( this.currentSection!.name, this.pageIndex + 1 );
            return;
        }

        // We're at the end, so back to start
        if( this.isAtEnd )
        {
            this.reset();
            return;
        }

        // Advance to the next section and reset the page to zero
        const sectionIndex = this.sections.findIndex( s => s.name == this.active );
        if( sectionIndex < this.sections.length - 1 )
        {
            this.goto( this.sections[ sectionIndex + 1 ].name );
        }
    }

    /**
     * Initialise the slides for the presentation
     * @param sections 
     */
    private initialiseSlides( sections: SectionMeta[] | { name: string }[] ) : SectionMeta[]
    {

        const slides = this.unpackSlides( sections );

        // Add the slide sorter
        slides.push( { 
            name: DefaultSections.SlideSorter, 
            title: 'All Slides',
            displayPageTitle: false,
            isAppendix: true,
            isHidden: true,
            hasTitlePage: false,
            pages: [ { title: '', component: SlideSorter } ],
            images: []   
        });

        // Insert a title page for any section that has it enabled, update page titles and screenshots
        for( const section of slides )
        {
            if( section.hasTitlePage )
            {
                section.pages.unshift( { title: section.title, component: Title } );
            }
            for( const [index, page] of section.pages.entries() )
            {
                if( ( page.title ?? '' ).length == 0 ) page.title = section.title;
                if( ( page.screenshot ?? '' ).length == 0 ) page.screenshot = `/images/${ section.isShared ? 'shared' : this.key}/Screenshots/${section.name}-${index}.jpg`;
            }
        }

        // Mark all slide components as Raw or they will get double-ref'd when mounted
        slides.forEach( s => s.pages.forEach( p => p.component = markRaw( p.component ) ) );
        return slides;

    }

    /**
     * Combines shared slides into the section definitions
     */
    private unpackSlides( sections: SectionMeta[] | { name: string }[] ) : SectionMeta[]
    {

        const sharedSlides = useSharedSlides();
        const result = [] as SectionMeta[];

        for( const section of sections )
        {
            if( Object.keys( section ).length == 1 )
            {
                const sharedSlide = sharedSlides.find( s => s.name == section.name ) as SectionMeta;
                if( sharedSlide != null ) 
                {
                    const copy = Object.assign( {}, sharedSlide );
                    copy.isShared = true;
                    result.push( copy );
                }
            }

            else
            {
                const copy = Object.assign( {}, section );
                result.push( copy as SectionMeta );
            }
        }

        return result;

    }

    /**
     * Preload the images
     */
    private preloadImages()
    {

        this.imageCount = this.sections.reduce( ( a, s ) => a += s.images.length, 0 );
        if( this.imageCount == 0 )
        {
            this.isImagePreloadComplete = true;
            return;
        }

        for( const section of this.sections )
        {
            for( const path of section.images )
            {
                const img = new Image();
                const pathKey = section.isShared ? 'shared' : this.key;
                
                img.src = 
                    path.startsWith( '~/images/' ) ? path.replace( '~', '' ) :
                    path.startsWith( '~/' ) ? path.replace( '~/', '~/images/' ) :
                    path.startsWith( '/images/' ) ? path :
                    `/images/${pathKey}/` + ( path.startsWith( '/' ) ? path.substring( 1 ) : path );

                img.onload = () => 
                {
                    if( ++this.imagesLoaded < this.imageCount ) return;
                    this.isImagePreloadComplete = true;
                }
            }
        }

    }

    /**
     * Display the given video or image
     * @param src 
     */
    displayMedia( src: string )
    {

        const pathKey = this.currentSection.isShared ? 'shared' : this.key;
        src = src.startsWith( '~/images/' ) ? src.replace( '~', '' ) :
              src.startsWith( '~/video' ) ? src.replace( '~/video', '/video' ) :
              src.startsWith( '/video/' ) ? src :
              src.startsWith( '~/' ) ? src.replace( '~/', '~/images/' ) :
              src.startsWith( '/images/' ) ? src :
              `/images/${pathKey}/` + ( src.startsWith( '/' ) ? src.substring( 1 ) : src );

        this.mediaSrc = src;
        this.showMedia = true;

    }

});